import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Target, Trophy, Plus, Trash, UsersThree, Crown, CaretDown, CaretUp } from '@phosphor-icons/react';
import QuarterlyBonusSummary from './QuarterlyBonusSummary';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const TargetsPage = () => {
  const [tab, setTab] = useState('targets');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  // Targets state
  const [targets, setTargets] = useState([]);
  const [targetMonth, setTargetMonth] = useState(currentMonth);
  const [targetYear, setTargetYear] = useState(currentYear);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetForm, setTargetForm] = useState({ user_id: '', team_name: '', target_type: 'user', target_placements: '', target_revenue: '' });

  // Quarterly bonuses state
  const [bonuses, setBonuses] = useState([]);
  const [bonusQuarter, setBonusQuarter] = useState(`Q${Math.ceil(currentMonth / 3)}-${currentYear}`);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusForm, setBonusForm] = useState({ user_id: '', amount: '', quarter: bonusQuarter, reason: '' });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(res.data.filter(u => !['superadmin', 'ceo'].includes(u.role)));
    } catch (e) { /* ignore */ }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teams`, { withCredentials: true });
      setTeams(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchTargets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/targets/progress?month=${targetMonth}&year=${targetYear}`, { withCredentials: true });
      setTargets(res.data);
    } catch (e) { toast.error('Failed to load targets'); }
  }, [targetMonth, targetYear]);

  const fetchBonuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quarterly-bonuses?quarter=${bonusQuarter}`, { withCredentials: true });
      setBonuses(res.data);
    } catch (e) { toast.error('Failed to load bonuses'); }
  }, [bonusQuarter]);

  useEffect(() => { fetchUsers(); fetchTeams(); }, [fetchUsers, fetchTeams]);
  useEffect(() => { if (tab === 'targets') fetchTargets(); }, [tab, fetchTargets]);
  useEffect(() => { if (tab === 'bonuses') fetchBonuses(); }, [tab, fetchBonuses]);

  const handleSetTarget = async () => {
    const payload = {
      month: targetMonth,
      year: targetYear,
      target_placements: parseInt(targetForm.target_placements) || 0,
      target_revenue: parseInt(targetForm.target_revenue) || 0,
    };
    if (targetForm.target_type === 'user') payload.user_id = targetForm.user_id;
    else payload.team_name = targetForm.team_name;

    if (!payload.user_id && !payload.team_name) { toast.error('Select a user or team'); return; }

    try {
      await axios.post(`${API_URL}/api/admin/targets`, payload, { withCredentials: true });
      setShowTargetModal(false);
      toast.success('Target set');
      fetchTargets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAwardBonus = async () => {
    if (!bonusForm.user_id || !bonusForm.amount) { toast.error('User and amount required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/quarterly-bonuses`, {
        user_id: bonusForm.user_id,
        amount: parseFloat(bonusForm.amount),
        quarter: bonusForm.quarter,
        reason: bonusForm.reason,
      }, { withCredentials: true });
      setShowBonusModal(false);
      setBonusForm({ user_id: '', amount: '', quarter: bonusQuarter, reason: '' });
      toast.success('Bonus awarded!');
      fetchBonuses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRevokeBonus = async (id) => {
    if (!window.confirm('Revoke this bonus? This will also remove it from the ledger.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/quarterly-bonuses/${id}`, { withCredentials: true });
      toast.success('Bonus revoked');
      fetchBonuses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="targets-bonuses-page">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900">Targets & Performance Bonuses</h1>
        <p className="text-sm text-slate-500 mt-1">Set monthly placement targets for individuals and teams. Track achievement and award quarterly bonuses based on performance.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button variant={tab === 'targets' ? 'default' : 'outline'} onClick={() => setTab('targets')} className="gap-2" data-testid="tab-targets">
          <Target size={18} /> Monthly Targets
        </Button>
        <Button variant={tab === 'bonuses' ? 'default' : 'outline'} onClick={() => setTab('bonuses')} className="gap-2" data-testid="tab-bonuses">
          <Trophy size={18} /> Quarterly Bonuses
        </Button>
        <Button variant={tab === 'teams' ? 'default' : 'outline'} onClick={() => setTab('teams')} className="gap-2" data-testid="tab-teams">
          <UsersThree size={18} /> Team Structure
        </Button>
      </div>

      {tab === 'targets' && (
        <>
          {/* Month/Year selector + Add button */}
          <div className="flex items-center gap-4">
            <Select value={String(targetMonth)} onValueChange={(v) => setTargetMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(targetYear)} onValueChange={(v) => setTargetYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[currentYear - 1, currentYear, currentYear + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => { setTargetForm({ user_id: '', team_name: '', target_type: 'user', target_placements: '', target_revenue: '' }); setShowTargetModal(true); }} data-testid="btn-add-target">
              <Plus size={16} className="mr-1" /> Set Target
            </Button>
          </div>

          {/* Target cards */}
          {targets.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">No targets set for {MONTHS[targetMonth - 1]} {targetYear}</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {targets.map((t) => (
                <Card key={t.id} className="border-slate-200">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-slate-900">{t.target_name}</p>
                        <p className="text-xs text-slate-500">{t.user_id ? 'Individual' : 'Team'} Target</p>
                      </div>
                      <Badge className={t.achievement_pct >= 100 ? 'bg-emerald-100 text-emerald-700' : t.achievement_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
                        {t.achievement_pct}%
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Placements</span>
                        <span className="font-medium">{t.actual_placements || 0} / {t.target_placements}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${Math.min(t.achievement_pct, 100)}%` }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'bonuses' && (
        <>
          {/* Auto-Calculated Quarterly Bonus Summary */}
          <QuarterlyBonusSummary />

          {/* Manual Bonus Awards */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Manual Bonus Awards</h3>
              <div className="flex items-center gap-4">
                <Select value={bonusQuarter} onValueChange={setBonusQuarter}>
                  <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map(q => [currentYear - 1, currentYear, currentYear + 1].map(y => (
                      <SelectItem key={`Q${q}-${y}`} value={`Q${q}-${y}`}>Q{q}-{y}</SelectItem>
                    ))).flat()}
                  </SelectContent>
                </Select>
                <Button onClick={() => { setBonusForm({ user_id: '', amount: '', quarter: bonusQuarter, reason: '' }); setShowBonusModal(true); }} data-testid="btn-award-bonus">
                  <Plus size={16} className="mr-1" /> Award Bonus
                </Button>
              </div>
            </div>

          {bonuses.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">No manual bonuses awarded for {bonusQuarter}</CardContent></Card>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Recipient</th>
                      <th className="text-left p-3 font-medium text-slate-600">Role</th>
                      <th className="text-right p-3 font-medium text-slate-600">Amount</th>
                      <th className="text-left p-3 font-medium text-slate-600">Reason</th>
                      <th className="text-left p-3 font-medium text-slate-600">Awarded By</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bonuses.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-50" data-testid={`bonus-row-${b.id}`}>
                        <td className="p-3 font-medium">{b.user_name}</td>
                        <td className="p-3 text-slate-600">{b.user_role?.replace(/_/g, ' ')}</td>
                        <td className="p-3 text-right font-semibold text-emerald-700">₹{b.amount?.toLocaleString('en-IN')}</td>
                        <td className="p-3 text-slate-600 max-w-[200px] truncate">{b.reason || '-'}</td>
                        <td className="p-3 text-slate-500">{b.awarded_by_name}</td>
                        <td className="p-3">
                          <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600" onClick={() => handleRevokeBonus(b.id)} data-testid={`btn-revoke-${b.id}`}>
                            <Trash size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
          </div>
        </>
      )}

      {tab === 'teams' && (
        <TeamsStructureTab teams={teams} users={users} targets={targets} targetMonth={targetMonth} targetYear={targetYear} />
      )}

      {/* Set Target Modal */}
      <Dialog open={showTargetModal} onOpenChange={setShowTargetModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set Monthly Target ({MONTHS[targetMonth - 1]} {targetYear})</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-4">
              <Button variant={targetForm.target_type === 'user' ? 'default' : 'outline'} size="sm" onClick={() => setTargetForm({ ...targetForm, target_type: 'user', team_name: '' })}>Individual</Button>
              <Button variant={targetForm.target_type === 'team' ? 'default' : 'outline'} size="sm" onClick={() => setTargetForm({ ...targetForm, target_type: 'team', user_id: '' })}>Team</Button>
            </div>
            {targetForm.target_type === 'user' ? (
              <div className="space-y-2">
                <Label>User</Label>
                <Select value={targetForm.user_id} onValueChange={(v) => setTargetForm({ ...targetForm, user_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role?.replace(/_/g, ' ')})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Team</Label>
                <Select value={targetForm.team_name} onValueChange={(v) => setTargetForm({ ...targetForm, team_name: v })}>
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Placements</Label>
                <Input type="number" value={targetForm.target_placements} onChange={(e) => setTargetForm({ ...targetForm, target_placements: e.target.value })} placeholder="e.g. 5" data-testid="input-target-placements" />
              </div>
              <div className="space-y-2">
                <Label>Target Revenue (₹)</Label>
                <Input type="number" value={targetForm.target_revenue} onChange={(e) => setTargetForm({ ...targetForm, target_revenue: e.target.value })} placeholder="e.g. 500000" data-testid="input-target-revenue" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTargetModal(false)}>Cancel</Button>
            <Button onClick={handleSetTarget} data-testid="btn-confirm-target">Set Target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award Bonus Modal */}
      <Dialog open={showBonusModal} onOpenChange={setShowBonusModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Award Quarterly Bonus</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient</Label>
              <Select value={bonusForm.user_id} onValueChange={(v) => setBonusForm({ ...bonusForm, user_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role?.replace(/_/g, ' ')})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input type="number" value={bonusForm.amount} onChange={(e) => setBonusForm({ ...bonusForm, amount: e.target.value })} placeholder="e.g. 25000" data-testid="input-bonus-amount" />
              </div>
              <div className="space-y-2">
                <Label>Quarter</Label>
                <Select value={bonusForm.quarter} onValueChange={(v) => setBonusForm({ ...bonusForm, quarter: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map(q => [currentYear - 1, currentYear, currentYear + 1].map(y => (
                      <SelectItem key={`Q${q}-${y}`} value={`Q${q}-${y}`}>Q{q}-{y}</SelectItem>
                    ))).flat()}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={bonusForm.reason} onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })} placeholder="Performance reason..." rows={3} data-testid="input-bonus-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBonusModal(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleAwardBonus} data-testid="btn-confirm-bonus">Award Bonus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TargetsPage;

// Teams Structure Tab component
const TeamsStructureTab = ({ teams, users, targets, targetMonth, targetYear }) => {
  const [expandedTeams, setExpandedTeams] = useState({});
  const [editingTarget, setEditingTarget] = useState(null); // {type: 'team'|'user', id, teamName}
  const [targetValue, setTargetValue] = useState('');
  const [saving, setSaving] = useState(false);
  const toggleTeam = (id) => setExpandedTeams(p => ({ ...p, [id]: !p[id] }));

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const userMap = {};
  for (const u of users) { if (u.id) userMap[u.id] = u; }

  // Find target count for a specific team for the selected month/year
  const getTeamTargetCount = (teamName) => {
    const t = targets.find(t => t.team_name === teamName && String(t.month) === String(targetMonth) && String(t.year) === String(targetYear));
    return t?.target_count || t?.target_placements || 0;
  };

  // Find target count for a user
  const getUserTargetCount = (userId) => {
    const t = targets.find(t => t.user_id === userId && String(t.month) === String(targetMonth) && String(t.year) === String(targetYear));
    return t?.target_count || t?.target_placements || 0;
  };

  const hasTeamTarget = (teamName) => targets.some(t => t.team_name === teamName && String(t.month) === String(targetMonth) && String(t.year) === String(targetYear));
  const hasUserTarget = (userId) => targets.some(t => t.user_id === userId && String(t.month) === String(targetMonth) && String(t.year) === String(targetYear));

  // Count targets per team and per individual (across all months)
  const teamTargetCount = {};
  const userTargetCountAll = {};
  for (const t of targets) {
    if (t.team_name) teamTargetCount[t.team_name] = (teamTargetCount[t.team_name] || 0) + 1;
    if (t.user_id) userTargetCountAll[t.user_id] = (userTargetCountAll[t.user_id] || 0) + 1;
  }

  const saveTarget = async () => {
    if (!editingTarget) return;
    setSaving(true);
    try {
      const count = parseInt(targetValue) || 0;
      if (editingTarget.type === 'team') {
        await axios.post(`${API_URL}/api/admin/targets/team-targets`, {
          team_name: editingTarget.teamName,
          month: parseInt(targetMonth),
          year: parseInt(targetYear),
          target_count: count,
          target_revenue: count * 15000,
        }, { withCredentials: true });
      } else {
        // Individual user target
        await axios.post(`${API_URL}/api/admin/targets`, {
          team_name: editingTarget.teamName,
          user_id: editingTarget.id,
          month: parseInt(targetMonth),
          year: parseInt(targetYear),
          target_placements: count,
          target_revenue: count * 15000,
        }, { withCredentials: true });
      }
      toast.success('Target saved');
      // Reload page to refresh targets
      window.location.reload();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save target');
    }
    finally { setSaving(false); setEditingTarget(null); setTargetValue(''); }
  };

  const startEdit = (type, id, teamName, currentCount) => {
    setEditingTarget({ type, id, teamName });
    setTargetValue(String(currentCount || ''));
  };

  // Group teams by manager
  const managerGroups = {};
  for (const team of teams) {
    const mgrId = team.manager_id;
    const mgr = userMap[mgrId];
    const mgrName = mgr?.name || 'Unassigned Manager';
    if (!managerGroups[mgrName]) managerGroups[mgrName] = { manager: mgr, teams: [] };
    managerGroups[mgrName].teams.push(team);
  }

  const getRoleLabel = (role) => {
    const map = { sales_recruiter: 'Recruiter', sales_technical_recruiter: 'Recruiter', sales_lead: 'Lead', sales_manager: 'Manager', sales_assistant_manager: 'Asst. Mgr', technical_recruiter: 'Recruiter', recruiting_lead: 'Lead', recruiting_manager: 'Manager', recruiting_assistant_manager: 'Asst. Mgr' };
    return map[role] || role?.replace(/_/g, ' ');
  };

  if (teams.length === 0) {
    return <Card><CardContent className="p-8 text-center text-slate-500">No teams found. Create teams from Teams & Analytics page.</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="teams-structure-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-slate-200"><CardContent className="p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Teams</p><p className="text-xl font-bold text-slate-800">{teams.length}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Total Members</p><p className="text-xl font-bold text-blue-700">{teams.reduce((s, t) => s + (t.member_ids?.length || 0), 0)}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Team Targets Set</p><p className="text-xl font-bold text-emerald-700">{Object.keys(teamTargetCount).length}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3"><p className="text-[10px] uppercase text-slate-500 font-semibold">Individual Targets</p><p className="text-xl font-bold text-purple-700">{Object.keys(userTargetCountAll).length}</p></CardContent></Card>
      </div>

      <p className="text-xs text-slate-500">Click the target count or "Set" button to add/edit targets for the selected month/year. Changes are saved immediately.</p>

      {/* Teams hierarchy */}
      {Object.entries(managerGroups).map(([mgrName, group]) => (
        <Card key={mgrName} className="border-slate-200 overflow-hidden" data-testid={`target-mgr-group-${mgrName}`}>
          <div className="bg-slate-800 text-white px-5 py-2.5 flex items-center gap-3">
            <Crown size={18} className="text-amber-400" />
            <div className="flex-1">
              <p className="font-semibold text-sm">{mgrName}</p>
              <p className="text-[10px] text-slate-300 uppercase tracking-wider">{group.teams.length} team{group.teams.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {group.teams.map((team) => {
              const isExpanded = expandedTeams[team.id];
              const lead = userMap[team.lead_id];
              const memberIds = team.member_ids || [];
              const teamTargetCt = getTeamTargetCount(team.name);
              const hasTeamTgt = hasTeamTarget(team.name);

              return (
                <div key={team.id} data-testid={`target-team-${team.id}`}>
                  <div className="px-5 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3" onClick={() => toggleTeam(team.id)}>
                    <button className="text-slate-400 shrink-0">{isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}</button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-sm">{team.name}</span>
                        <Badge className={`text-[10px] ${team.department === 'sales' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>{team.department}</Badge>
                        <Badge variant="outline" className="text-[10px]">{memberIds.length} members</Badge>
                      </div>
                      {lead && <p className="text-[11px] text-slate-500 mt-0.5">Lead: <strong className="text-slate-700">{lead.name}</strong></p>}
                    </div>
                    <div className="flex gap-4 text-xs items-center">
                      <div className="text-center">
                        <p className="text-[10px] text-slate-400 uppercase">Team Target</p>
                        {editingTarget?.type === 'team' && editingTarget?.teamName === team.name ? (
                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            <Input type="number" value={targetValue} onChange={e => setTargetValue(e.target.value)}
                              className="h-6 w-16 text-xs px-1" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveTarget(); if (e.key === 'Escape') setEditingTarget(null); }}
                              data-testid={`team-target-input-${team.name}`} />
                            <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600" onClick={saveTarget} disabled={saving}>Save</Button>
                          </div>
                        ) : (
                          <p className={`font-semibold cursor-pointer hover:text-blue-600 ${hasTeamTgt ? 'text-emerald-700' : 'text-slate-400'}`}
                            onClick={e => { e.stopPropagation(); startEdit('team', team.id, team.name, teamTargetCt); }}
                            data-testid={`team-target-edit-${team.name}`}>
                            {hasTeamTgt ? teamTargetCt : 'Set'}
                          </p>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-slate-400 uppercase">Individual Targets</p>
                        <p className="font-semibold text-purple-700">{memberIds.filter(mid => hasUserTarget(mid)).length} / {memberIds.length}</p>
                      </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="bg-slate-50/70 border-t border-slate-100">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-100/80">
                            <th className="px-4 py-1.5 text-left font-semibold">Name</th>
                            <th className="px-3 py-1.5 text-left font-semibold">Role</th>
                            <th className="px-3 py-1.5 text-center font-semibold">Target Count</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {memberIds.map(mid => {
                            const m = userMap[mid];
                            if (!m) return null;
                            const userTgtCt = getUserTargetCount(mid);
                            const hasUserTgt = hasUserTarget(mid);
                            const isEditing = editingTarget?.type === 'user' && editingTarget?.id === mid;

                            return (
                              <tr key={mid} className="hover:bg-white/80" data-testid={`target-member-${mid}`}>
                                <td className="px-4 py-2 font-medium text-slate-800">{m.name}</td>
                                <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{getRoleLabel(m.role)}</Badge></td>
                                <td className="px-3 py-2 text-center">
                                  {isEditing ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <Input type="number" value={targetValue} onChange={e => setTargetValue(e.target.value)}
                                        className="h-6 w-16 text-xs px-1" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveTarget(); if (e.key === 'Escape') setEditingTarget(null); }}
                                        data-testid={`user-target-input-${mid}`} />
                                      <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600" onClick={saveTarget} disabled={saving}>Save</Button>
                                    </div>
                                  ) : (
                                    <span className={`cursor-pointer hover:text-blue-600 font-semibold ${hasUserTgt ? 'text-emerald-700' : 'text-slate-400'}`}
                                      onClick={() => startEdit('user', mid, team.name, userTgtCt)}
                                      data-testid={`user-target-edit-${mid}`}>
                                      {hasUserTgt ? userTgtCt : 'Set'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {memberIds.length === 0 && <p className="px-4 py-3 text-xs text-slate-400 text-center">No members</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
};
